import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import isBot from 'Helpers/SEO/is-bot';
import serverSkeleton from 'Components/Progress/serverSkeleton';

// Longest the skeleton is kept up after the app mounts, for images the page never replaces (e.g. a campaign that
// ended after the worker cached its data)
const HOLD_CAP_MS = 1500;

// How much of each other's box (in the viewport) a page image and a skeleton image must cover for the page image to
// count as replacing it. Mutual, so a card in front of a full width background doesn't count as replacing it
const MIN_OVERLAP = 0.5;

// The element's box in the viewport, with the area it has there, and its whole size
const viewportRect = (element) => {
  const box = element.getBoundingClientRect();
  const { top, right, bottom, left } = box;
  const clipped = {
    top: Math.max(top, 0),
    right: Math.min(right, window.innerWidth),
    bottom: Math.min(bottom, window.innerHeight),
    left: Math.max(left, 0),
  };
  const width = Math.max(0, clipped.right - clipped.left);
  const height = Math.max(0, clipped.bottom - clipped.top);
  return { ...clipped, area: width * height, size: box.width * box.height };
};

const overlap = (a, b) => (
  Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left)) *
  Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top))
);

const imagePath = (img) => {
  try {
    return new URL(img.currentSrc || img.src).pathname;
  } catch (e) {
    return '';
  }
};

const isLoaded = img => img.complete && img.naturalWidth > 0;

/**
 * The page images that replace the skeleton's images in the viewport, or null if any of those isn't ready yet. A
 * page image replaces a skeleton image if it's the same file at a similar size (not a thumbnail of it elsewhere), or
 * it's in about the same place: the page's data may have a different image there (e.g. another first carousel card).
 * @param  {Element} skeletonElement The held skeleton
 * @param  {Element} pageElement     The page underneath
 * @return {Array.Element|null}
 */
const replacements = (skeletonElement, pageElement) => {
  // Not shown yet: the app's root is hidden until AppInitializer mounts
  if (!skeletonElement.offsetHeight) {
    return null;
  }

  const pageImages = [...pageElement.querySelectorAll('img')]
    // SkeletonPlaceholder's copy of the skeleton, while the page or its data loads
    .filter(img => isLoaded(img) && !img.closest('.skeleton-placeholder'))
    .map(img => ({ img, path: imagePath(img), rect: viewportRect(img) }))
    .filter(({ img }) => img.offsetWidth > 0);

  // Slots the worker filled, loaded or not; unfilled ones have neither attribute
  const skeletonImages = [...skeletonElement.querySelectorAll('img')]
    .filter(img => img.getAttribute('src') || img.getAttribute('srcset'))
    .map(img => ({ path: imagePath(img), rect: viewportRect(img) }))
    .filter(({ rect }) => rect.area > 0);

  const sameFile = (skeletonImage, pageImage) => pageImage.path === skeletonImage.path
    && pageImage.rect.size >= MIN_OVERLAP * skeletonImage.rect.size;
  const samePlace = (skeletonImage, pageImage) => {
    const shared = overlap(skeletonImage.rect, pageImage.rect);
    return shared >= MIN_OVERLAP * skeletonImage.rect.area
      && shared >= MIN_OVERLAP * pageImage.rect.area;
  };

  // Each skeleton image needs its own replacement (e.g. a take over's foreground and background
  // share a box): the same file first, then the unused page image that overlaps it most
  const found = [];
  const unused = pageImage => !found.includes(pageImage);
  skeletonImages.forEach((skeletonImage, i) => {
    found[i] = pageImages
      .find(pageImage => unused(pageImage) && sameFile(skeletonImage, pageImage));
  });
  skeletonImages.forEach((skeletonImage, i) => {
    if (!found[i]) {
      const byOverlap = (a, b) => (
        overlap(skeletonImage.rect, b.rect) - overlap(skeletonImage.rect, a.rect)
      );
      [found[i]] = pageImages
        .filter(pageImage => unused(pageImage) && samePlace(skeletonImage, pageImage))
        .sort(byOverlap);
    }
  });

  return found.every(Boolean) ? found.map(({ img }) => img) : null;
};

/**
 * Keeps the page skeleton up after the app mounts, on the first page only, until the page has replaced its images,
 * so they don't disappear while the page waits for its data and reappear when it has it. The page renders underneath
 * at its real size but hidden, so its images load, and its layout shifts while hidden don't count towards CLS. The
 * skeleton goes in one frame once each skeleton image in the viewport has a loaded and decoded replacement, after
 * HOLD_CAP_MS at most, or straight away on navigation.
 * It renders the app's content area (`className`). Released, the page's wrapper is `display: contents`, so the page
 * lays out as if it weren't there.
 */
export default function SkeletonHold({ className, children }) {
  const [skeleton] = useState(() => (!isBot() && serverSkeleton()) || null);
  const [holding, setHolding] = useState(!!skeleton);
  const skeletonRef = useRef();
  const pageRef = useRef();

  useEffect(() => {
    if (!holding) {
      return undefined;
    }

    let frame;
    let released = false;
    const release = () => {
      if (!released) {
        released = true;
        setHolding(false);
      }
    };

    const cap = setTimeout(release, HOLD_CAP_MS);

    const check = () => {
      if (released) {
        return;
      }

      if (window.location.pathname !== skeleton.path) {
        release();
        return;
      }

      const images = skeletonRef.current && pageRef.current
        && replacements(skeletonRef.current, pageRef.current);
      if (images) {
        // Decoded, so the page's images show in the same frame as the skeleton goes (where browsers can say)
        const decoded = img => (img.decode ? img.decode().catch(() => {}) : Promise.resolve());
        Promise.all(images.map(decoded)).then(release);
        return;
      }

      frame = requestAnimationFrame(check);
    };

    frame = requestAnimationFrame(check);

    return () => {
      released = true;
      clearTimeout(cap);
      cancelAnimationFrame(frame);
    };
  }, [holding, skeleton]);

  return (
    <div className={classNames(className, { 'SkeletonHold--holding': holding })}>
      {holding &&
        // eslint-disable-next-line react/no-danger
        <div ref={skeletonRef} className="SkeletonHold__skeleton" dangerouslySetInnerHTML={{ __html: skeleton.html }} />
      }
      <div ref={pageRef} className="SkeletonHold__page">
        {children}
      </div>
    </div>
  );
}

SkeletonHold.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};

SkeletonHold.defaultProps = {
  className: '',
};
